// HR-only "Edit Attendance" page.
//
// HR filters check-in records by employee(s) + a PKT date range, sees check-in AND
// check-out, fixes either time, fills in missed check-outs, and adds an entry for an
// employee who has none. Backend: /attendance-admin/records (GET / PATCH / POST),
// all HR-only. Every check-in/check-out value follows the "fake UTC" convention —
// see ./attendanceTime.js; this file never applies a timezone offset to them.

import React, { useCallback, useEffect, useRef, useState } from "react";
import "../SearchContainer.css";
import "./attendanceEditor.css";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import MoreTimeOutlinedIcon from "@mui/icons-material/MoreTimeOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  HOUR_MS,
  MAX_NOTE,
  SELF_CHECKOUT_WINDOW_MS,
  dayDiff,
  evaluateEntry,
  fmtDate,
  fmtDateMs,
  fmtDuration,
  fmtRealPkt,
  fmtTime,
  fmtTimeMs,
  isoToWall,
  normalizeWall,
  parseWall,
  pktNowWallMs,
  pktToday,
  pktYesterday,
  validateRange,
  wallFromMs,
} from "./attendanceTime";

const API = "https://hr-backend-72v1.onrender.com";
const JSON_HEADERS = { "Content-Type": "application/json" };
const COLUMN_COUNT = 7;

const goldSwitchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#D4AF37" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#D4AF37",
    opacity: 0.55,
  },
};

const goldProgressSx = {
  height: 3,
  borderRadius: 3,
  backgroundColor: "rgba(212,175,55,0.15)",
  "& .MuiLinearProgress-bar": { backgroundColor: "#D4AF37" },
};

const findButtonSx = {
  color: "var(--accent)",
  minWidth: "120px",
  height: "40px",
  borderColor: "rgba(212,175,55,0.4)",
  "&:hover": {
    borderColor: "rgba(212,175,55,0.7)",
    backgroundColor: "rgba(212,175,55,0.08)",
  },
};

const STATUS_META = {
  accepted: { label: "Accepted", kind: "accepted" },
  pending: { label: "Pending manager", kind: "pending" },
  rejected: { label: "Rejected", kind: "rejected" },
};

const employeeLabel = (o) =>
  o ? (o.name ? `${o.name} (${o.email})` : o.email || "") : "";

const sameEmployee = (o, v) => Boolean(o && v && o.email === v.email);

const initialsOf = (name) =>
  String(name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("") || "?";

async function readErrorMessage(res, fallback) {
  if (res.status === 403) return "Only HR can edit attendance";
  if (res.status === 401) return "Your session expired. Please log in again.";
  try {
    const data = await res.json();
    if (data && typeof data.message === "string" && data.message) return data.message;
    if (data && typeof data.error === "string" && data.error) return data.error;
  } catch (e) {
    /* non-JSON body */
  }
  return fallback;
}

/** Open record the employee can still close themselves (inside check-in + 12h). */
function isStillOpen(record, nowWallMs) {
  if (!record || record.checkOut) return false;
  const inMs = Date.parse(record.checkIn);
  return !Number.isNaN(inMs) && nowWallMs - inMs < SELF_CHECKOUT_WINDOW_MS;
}

/** Would this record show up under the given (applied) filters? */
function matchesFilters(record, filters) {
  if (!record || !filters) return true;
  const day = isoToWall(record.checkIn).slice(0, 10);
  if (!day || day < filters.from || day > filters.to) return false;
  if (filters.emails.length && !filters.emails.includes(record.email)) return false;
  if (filters.missingOnly && record.checkOut) return false;
  return true;
}

function editSummary(record) {
  const e = record.lastEdit;
  const verb = e && e.action === "create" ? "Added" : "Edited";
  if (!e) return `${verb} by HR`;
  const who = e.byName || e.by || "HR";
  const when = e.at ? fmtRealPkt(e.at) : "";
  let text = `${verb} by ${who}${when ? ` on ${when}` : ""}`;
  if (e.note) text += `: ${e.note}`;
  if (record.editCount > 1) text += ` (${record.editCount} changes in total)`;
  return text;
}

const StatusPill = ({ status }) => {
  if (!status) return <span className="aeDash">—</span>;
  const meta = STATUS_META[status] || {
    label: status.charAt(0).toUpperCase() + status.slice(1),
    kind: "neutral",
  };
  return <span className={`aePill aePill--${meta.kind}`}>{meta.label}</span>;
};

/* ─────────────────────────── Edit / Add dialog ─────────────────────────── */

const EntryDialog = ({
  mode,
  record,
  employees,
  employeesError,
  defaultEmployee,
  onClose,
  onSaved,
  onStale,
}) => {
  const isEdit = mode === "edit";
  const origIn = isEdit ? isoToWall(record.checkIn) : "";
  const origOut = isEdit ? isoToWall(record.checkOut) : "";
  const hadCheckOut = isEdit && Boolean(record.checkOut);

  const [employee, setEmployee] = useState(isEdit ? null : defaultEmployee || null);
  const [checkIn, setCheckIn] = useState(origIn);
  const [checkOut, setCheckOut] = useState(origOut);
  const [note, setNote] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState("");

  const nowWall = pktNowWallMs();
  // A field left as it was keeps its stored value on the server (seconds included), so the
  // preview uses that exact value too — see evaluateEntry.
  const storedInMs = isEdit ? Date.parse(record.checkIn) : NaN;
  const storedOutMs = isEdit && record.checkOut ? Date.parse(record.checkOut) : NaN;
  const check = evaluateEntry({
    checkIn,
    checkOut,
    checkOutRequired: !isEdit || hadCheckOut,
    requiredCheckOutMessage: hadCheckOut
      ? "A recorded check-out can't be cleared. Enter the correct time instead."
      : "Enter the check-out date and time.",
    nowWallMs: nowWall,
    inExactMs: isEdit && checkIn === origIn && !Number.isNaN(storedInMs) ? storedInMs : null,
    outExactMs:
      isEdit && hadCheckOut && checkOut === origOut && !Number.isNaN(storedOutMs)
        ? storedOutMs
        : null,
  });

  const unchanged = isEdit && checkIn === origIn && checkOut === origOut;
  const employeeError = !isEdit && !employee ? "Pick an employee." : "";
  const hasErrors = Boolean(
    check.errors.checkIn || check.errors.checkOut || employeeError || note.length > MAX_NOTE
  );

  // Format/order errors show as soon as a field has a value; "required" errors wait
  // for a save attempt — except clearing a recorded check-out, which is flagged at once.
  const inError =
    check.errors.checkIn && (attempted || checkIn !== "" || isEdit) ? check.errors.checkIn : "";
  const outError =
    check.errors.checkOut && (attempted || checkOut !== "" || hadCheckOut)
      ? check.errors.checkOut
      : "";
  const empError = attempted ? employeeError : "";

  const inMsForFill = parseWall(checkIn);
  const fillNineHours = () => {
    if (inMsForFill == null) return;
    setCheckOut(wallFromMs(inMsForFill + 9 * HOUR_MS));
    setServerError("");
  };

  // Self check-out: the employee's own Check Out only finds a shift that checked in within
  // the last 12h. Two cases worth telling HR about:
  //   - HR sets a check-out on a shift that is still live (their own button stops working);
  //   - the entry stays open — then the window runs from the check-in AS EDITED, and moving
  //     the check-in back can close that window without anyone noticing.
  const stillOpen = isEdit && isStillOpen(record, nowWall);
  const staysOpen = isEdit && !hadCheckOut && !checkOut;
  const openInMs = staysOpen && check.inMs != null ? check.inMs : null;
  const selfCheckoutDeadline =
    openInMs != null
      ? openInMs + SELF_CHECKOUT_WINDOW_MS
      : stillOpen
      ? Date.parse(record.checkIn) + SELF_CHECKOUT_WINDOW_MS
      : null;
  let selfCheckoutNotice = null;
  if (staysOpen && openInMs != null) {
    selfCheckoutNotice =
      selfCheckoutDeadline <= nowWall
        ? `With this check-in, ${record.name || "this employee"} can no longer check out on their own — their 12-hour window ended at ${fmtTimeMs(
            selfCheckoutDeadline
          )}, ${fmtDateMs(selfCheckoutDeadline)}. Add the check-out here.`
        : `${record.name || "This employee"} can still check out on their own until ${fmtTimeMs(
            selfCheckoutDeadline
          )}, ${fmtDateMs(selfCheckoutDeadline)}.`;
  } else if (stillOpen && checkOut) {
    selfCheckoutNotice = `${record.name || "This employee"} is still checked in and could check out on their own until ${fmtTimeMs(
      selfCheckoutDeadline
    )}, ${fmtDateMs(
      selfCheckoutDeadline
    )}. If you set a check-out now, their own Check Out button won't find this shift.`;
  }

  let preview = { kind: "muted", text: "—" };
  if (check.durationMs != null) {
    preview =
      check.status === "accepted"
        ? { kind: "accepted", text: "Will be Accepted" }
        : {
            kind: "pending",
            text:
              "Over 9h 25m — waits for the manager to approve the overtime (Manager Tab → Pending overtime)",
          };
  } else if (!checkOut && !check.errors.checkIn && !check.errors.checkOut && checkIn) {
    preview = { kind: "muted", text: "No check-out yet — status stays empty" };
  }

  const handleSave = async () => {
    setAttempted(true);
    setServerError("");
    if (saving || unchanged || hasErrors) return;
    setSaving(true);
    try {
      const body = { checkIn, checkOut: checkOut || null };
      const trimmed = note.trim();
      if (trimmed) body.note = trimmed;

      let res;
      if (isEdit) {
        // What this dialog was opened with — the server refuses the edit (409) if the entry
        // changed since, e.g. the employee checked out while the dialog was open.
        body.expectedCheckIn = record.checkIn || null;
        body.expectedCheckOut = record.checkOut || null;
        res = await fetch(
          `${API}/attendance-admin/records/${encodeURIComponent(record._id)}`,
          { method: "PATCH", headers: JSON_HEADERS, body: JSON.stringify(body) }
        );
      } else {
        res = await fetch(`${API}/attendance-admin/records`, {
          method: "POST",
          headers: JSON_HEADERS,
          body: JSON.stringify({ email: employee.email, ...body }),
        });
      }

      if (res.status === 409 && isEdit) {
        const data = await res.clone().json().catch(() => ({}));
        if (data && data.stale) {
          // The entry moved while this dialog was open (e.g. the employee checked out).
          // Retrying from this dialog can never succeed, so reload and start over.
          onStale(record.name || record.email);
          return;
        }
      }
      if (!res.ok) {
        const msg = await readErrorMessage(
          res,
          isEdit ? "Couldn't save the changes. Please try again." : "Couldn't add the entry. Please try again."
        );
        setServerError(msg);
        setSaving(false);
        return;
      }

      const data = await res.json().catch(() => ({}));
      onSaved({
        mode,
        record: data && data.record ? data.record : null,
        fallbackName: isEdit ? record.name : employee.name || employee.email,
      });
    } catch (e) {
      setServerError("Couldn't reach the server. Please check your connection and try again.");
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "aeDialog" }}
      aria-labelledby="ae-dialog-title"
    >
      <DialogTitle id="ae-dialog-title" className="aeDialogTitle">
        <span className="aeDialogIcon" aria-hidden="true">
          {isEdit ? <EditCalendarOutlinedIcon /> : <AddOutlinedIcon />}
        </span>
        {isEdit ? "Edit attendance" : "Add attendance entry"}
      </DialogTitle>

      <DialogContent className="aeDialogContent">
        {isEdit ? (
          <div className="aeWho">
            <div className="aeAvatar" aria-hidden="true">
              {initialsOf(record.name)}
            </div>
            <div className="aeWhoText">
              <div className="aeWhoName">{record.name || record.email}</div>
              <div className="aeWhoEmail">{record.email}</div>
            </div>
            <div className="aeWhoStatus">
              <span className="aeWhoLabel">Current status</span>
              <StatusPill status={record.status} />
            </div>
          </div>
        ) : (
          <Autocomplete
            className="aeDialogEmployee"
            options={employees}
            value={employee}
            onChange={(_e, v) => {
              setEmployee(v);
              setServerError("");
            }}
            getOptionLabel={employeeLabel}
            isOptionEqualToValue={sameEmployee}
            noOptionsText={employeesError ? "Couldn't load employees" : "No matching employees"}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <li key={key ?? option.email} {...rest}>
                  <span className="aeOptName">{option.name || option.email}</span>
                  {option.name ? <span className="aeOptEmail">({option.email})</span> : null}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Employee"
                error={Boolean(empError)}
                helperText={empError || " "}
              />
            )}
          />
        )}

        <div className="aeTimeGrid">
          <TextField
            label="Check-in (PKT)"
            type="datetime-local"
            value={checkIn}
            onChange={(e) => {
              setCheckIn(normalizeWall(e.target.value));
              setServerError("");
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 60 }}
            error={Boolean(inError)}
            helperText={inError || " "}
            fullWidth
          />
          <div className="aeCheckoutField">
            <TextField
              label="Check-out (PKT)"
              type="datetime-local"
              value={checkOut}
              onChange={(e) => {
                setCheckOut(normalizeWall(e.target.value));
                setServerError("");
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
              error={Boolean(outError)}
              helperText={
                outError ||
                (isEdit && !hadCheckOut && !checkOut
                  ? "Missing — leave empty to keep it open"
                  : " ")
              }
              fullWidth
            />
            <Button
              variant="text"
              size="small"
              className="aeQuickFill"
              startIcon={<MoreTimeOutlinedIcon />}
              onClick={fillNineHours}
              disabled={inMsForFill == null}
            >
              Check-in + 9h
            </Button>
          </div>
        </div>

        <div className="aePreview" aria-live="polite">
          <div className="aePreviewItem">
            <span className="aePreviewLabel">Duration</span>
            <span className="aePreviewValue">
              {check.durationMs != null ? fmtDuration(check.durationMs) : "—"}
              {check.nextDay > 0 ? (
                <span className="aeNextDay">ends next day</span>
              ) : null}
            </span>
          </div>
          <div className="aePreviewItem">
            <span className="aePreviewLabel">After saving</span>
            <span className={`aePreviewStatus aePreviewStatus--${preview.kind}`}>
              {preview.text}
            </span>
          </div>
        </div>

        {selfCheckoutNotice ? (
          <div className="aeNotice" role="note">
            <WarningAmberOutlinedIcon />
            <span>{selfCheckoutNotice}</span>
          </div>
        ) : null}

        <TextField
          className="aeNoteField"
          label="Note (optional)"
          placeholder="Why is this being changed? Saved in the edit history."
          multiline
          minRows={2}
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE))}
          inputProps={{ maxLength: MAX_NOTE }}
          helperText={`${note.length}/${MAX_NOTE}`}
          fullWidth
        />

        {serverError ? (
          <div className="aeFormError" role="alert">
            {serverError}
          </div>
        ) : null}
      </DialogContent>

      <DialogActions className="aeDialogActions">
        {unchanged ? <span className="aeHint">Change a time to save</span> : null}
        <Button variant="text" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || unchanged}
        >
          {saving ? "Saving…" : isEdit ? "Save changes" : "Add entry"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─────────────────────────────── Page ─────────────────────────────── */

const AttendanceEditor = ({ user }) => {
  const isHr = Boolean(user && user.role === "HR");
  // Yesterday → today (PKT): shifts are filed under their check-in date, so last night's
  // shift — where missed check-outs show up the next morning — is dated yesterday.
  const [initialRange] = useState(() => ({ from: pktYesterday(), to: pktToday() }));

  const [employees, setEmployees] = useState([]);
  const [employeesError, setEmployeesError] = useState(false);

  const [selected, setSelected] = useState([]);
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [missingOnly, setMissingOnly] = useState(false);
  const [filterError, setFilterError] = useState("");

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [dialog, setDialog] = useState(null); // { mode: "edit", record } | { mode: "create" }
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");

  const reqIdRef = useRef(0);
  const appliedRef = useRef(null);

  const load = useCallback(async (filters) => {
    const id = ++reqIdRef.current;
    appliedRef.current = filters;
    setLoading(true);
    setLoadError("");
    try {
      const params = new URLSearchParams();
      params.set("from", filters.from);
      params.set("to", filters.to);
      if (filters.emails.length) params.set("emails", filters.emails.join(","));
      if (filters.missingOnly) params.set("missingCheckout", "true");

      const res = await fetch(`${API}/attendance-admin/records?${params.toString()}`);
      if (!res.ok) {
        const msg = await readErrorMessage(res, "Couldn't load attendance. Please try again.");
        if (id !== reqIdRef.current) return;
        setRecords([]);
        setTotal(0);
        setTruncated(false);
        setLoadError(msg);
        return;
      }
      const data = await res.json();
      if (id !== reqIdRef.current) return;
      const list = Array.isArray(data && data.records) ? data.records : [];
      setRecords(list);
      setTotal(typeof data.total === "number" ? data.total : list.length);
      setTruncated(Boolean(data.truncated));
    } catch (e) {
      if (id !== reqIdRef.current) return;
      setRecords([]);
      setTotal(0);
      setTruncated(false);
      setLoadError("Couldn't reach the server. Please check your connection and try again.");
    } finally {
      if (id === reqIdRef.current) {
        setLoading(false);
        setHasLoaded(true);
      }
    }
  }, []);

  useEffect(() => {
    if (!isHr) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${API}/employees/emails`);
        if (!res.ok) throw new Error("employees");
        const data = await res.json();
        if (cancelled) return;
        const list = (Array.isArray(data) ? data : [])
          .filter((e) => e && typeof e.email === "string" && e.email)
          .sort((a, b) => String(a.name || a.email).localeCompare(String(b.name || b.email)));
        setEmployees(list);
      } catch (e) {
        if (!cancelled) setEmployeesError(true);
      }
    })();

    load({ from: initialRange.from, to: initialRange.to, emails: [], missingOnly: false });
    return () => {
      cancelled = true;
    };
  }, [isHr, load, initialRange]);

  const runSearch = (overrides = {}) => {
    const filters = {
      from,
      to,
      emails: selected.map((e) => e.email),
      missingOnly,
      ...overrides,
    };
    const err = validateRange(filters.from, filters.to);
    setFilterError(err);
    if (err) return;
    load(filters);
  };

  const handleFind = (e) => {
    if (e) e.preventDefault();
    runSearch();
  };

  const handleMissingToggle = (checked) => {
    setMissingOnly(checked);
    runSearch({ missingOnly: checked });
  };

  const handleReset = () => {
    const range = { from: pktYesterday(), to: pktToday() };
    setSelected([]);
    setFrom(range.from);
    setTo(range.to);
    setMissingOnly(false);
    setFilterError("");
    load({ ...range, emails: [], missingOnly: false });
  };

  const handleRetry = () => {
    if (appliedRef.current) load(appliedRef.current);
  };

  const openCreate = () => {
    setDialog({
      mode: "create",
      defaultEmployee: selected.length === 1 ? selected[0] : null,
    });
  };

  const handleSaved = ({ mode, record, fallbackName }) => {
    setDialog(null);
    const name = (record && record.name) || fallbackName || "the employee";
    const day = record ? ` (${fmtDate(record.checkIn)})` : "";
    let msg =
      mode === "edit" ? `Attendance updated for ${name}${day}` : `Entry added for ${name}${day}`;
    if (record && !matchesFilters(record, appliedRef.current)) {
      msg += " — it's outside the current filters";
    }
    setToastSeverity("success");
    setToastMsg(msg);
    setToastOpen(true);
    if (appliedRef.current) load(appliedRef.current);
  };

  const handleStale = (name) => {
    setDialog(null);
    setToastSeverity("warning");
    setToastMsg(
      `${name || "This entry"}'s attendance changed while you were editing (the employee may have just checked out). The list has been refreshed — open it again to make your change.`
    );
    setToastOpen(true);
    if (appliedRef.current) load(appliedRef.current);
  };

  const handleToastClose = (_e, reason) => {
    if (reason === "clickaway") return;
    setToastOpen(false);
  };

  if (!isHr) return null;

  const nowWall = pktNowWallMs();
  const missingCount = records.filter((r) => !r.checkOut && !isStillOpen(r, nowWall)).length;

  return (
    <div className="aeRoot">
      <div className="search-container aeFilters">
        <div className="night" aria-hidden="true">
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
        </div>

        <form className="aeFilterInner" onSubmit={handleFind} noValidate>
          <div className="aeFilterRow">
            <Autocomplete
              className="aeFieldEmployees"
              multiple
              options={employees}
              value={selected}
              onChange={(_e, v) => setSelected(v)}
              getOptionLabel={employeeLabel}
              isOptionEqualToValue={sameEmployee}
              filterSelectedOptions
              disableCloseOnSelect
              limitTags={3}
              noOptionsText={employeesError ? "Couldn't load employees" : "No matching employees"}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <li key={key ?? option.email} {...rest}>
                    <span className="aeOptName">{option.name || option.email}</span>
                    {option.name ? <span className="aeOptEmail">({option.email})</span> : null}
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key ?? option.email}
                      size="small"
                      label={option.name || option.email}
                      {...tagProps}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Employees"
                  placeholder={selected.length ? "" : "All employees"}
                />
              )}
            />
            <TextField
              className="aeFieldDate"
              label="From"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              className="aeFieldDate"
              label="To"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </div>

          <div className="aeFilterActions">
            <FormControlLabel
              className="aeSwitch"
              control={
                <Switch
                  checked={missingOnly}
                  onChange={(e) => handleMissingToggle(e.target.checked)}
                  sx={goldSwitchSx}
                />
              }
              label="Missed check-outs only"
            />
            <span className="aeSpacer" />
            <button
              type="button"
              className="searchReset aeReset"
              onClick={handleReset}
              title="Reset filters to yesterday and today"
            >
              <ReplayOutlinedIcon fontSize="small" />
              <span>Reset</span>
            </button>
            <Button
              type="submit"
              variant="outlined"
              className="searchFindBtn aeFindBtn"
              startIcon={<SearchOutlinedIcon />}
              sx={findButtonSx}
            >
              Find
            </Button>
          </div>

          {filterError ? (
            <div className="aeFilterError" role="alert">
              {filterError}
            </div>
          ) : null}
          {employeesError ? (
            <div className="aeFilterHint">
              Couldn't load the employee list. You can still search all employees.
            </div>
          ) : null}
        </form>
      </div>

      <TableContainer component={Paper} className="timerContainer aeCard">
        <h4 className="myTableHeader animate__animated animate__lightSpeedInLeft">
          EDIT ATTENDANCE
        </h4>

        <div className="aeToolbar">
          <div className="aeToolbarLeft">
            <span className="aeTzNote">
              <ScheduleOutlinedIcon />
              All times are Pakistan time (PKT)
            </span>
            {hasLoaded && !loadError ? (
              <span className="aeCount">
                {total} {total === 1 ? "entry" : "entries"}
                {missingCount > 0 ? (
                  <span className="aeCountMissing">
                    {" "}
                    · {missingCount} missing check-out{missingCount === 1 ? "" : "s"}
                  </span>
                ) : null}
              </span>
            ) : null}
          </div>
          <Button
            variant="contained"
            className="aeAddBtn"
            startIcon={<AddOutlinedIcon />}
            onClick={openCreate}
          >
            Add entry
          </Button>
        </div>

        {truncated ? (
          <div className="aeTruncated">
            Showing first {records.length} of {total}. Narrow the dates or pick employees to see
            the rest.
          </div>
        ) : null}

        <div className="aeProgressSlot">
          {loading ? <LinearProgress sx={goldProgressSx} /> : null}
        </div>

        <Table
          sx={{ minWidth: 940 }}
          className={`leaveTable aeTable${loading && records.length ? " aeTable--busy" : ""}`}
          aria-busy={loading}
        >
          <TableHead>
            <TableRow>
              <TableCell className="myTableHeadCells">
                <span className="myTableHeadSpan">Employee</span>
              </TableCell>
              <TableCell className="myTableHeadCells">
                <span className="myTableHeadSpan">Date</span>
              </TableCell>
              <TableCell className="myTableHeadCells">
                <span className="myTableHeadSpan">Check In</span>
              </TableCell>
              <TableCell className="myTableHeadCells">
                <span className="myTableHeadSpan">Check Out</span>
              </TableCell>
              <TableCell className="myTableHeadCells">
                <span className="myTableHeadSpan">Hours</span>
              </TableCell>
              <TableCell className="myTableHeadCells">
                <span className="myTableHeadSpan">Status</span>
              </TableCell>
              <TableCell className="myTableHeadCells" align="right">
                <span className="myTableHeadSpan">Edit</span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadError && !loading ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <div className="aeState aeState--error" role="alert">
                    <span>{loadError}</span>
                    <Button variant="outlined" size="small" onClick={handleRetry}>
                      Try again
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : null}

            {!loadError && records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <div className="aeState">
                    {loading || !hasLoaded ? (
                      <span>Loading attendance…</span>
                    ) : (
                      <>
                        <EventBusyOutlinedIcon />
                        <span>No attendance found for these filters</span>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : null}

            {records.map((r) => {
              const inMs = Date.parse(r.checkIn);
              const outMs = r.checkOut ? Date.parse(r.checkOut) : null;
              const open = !r.checkOut;
              const inWindow = open && isStillOpen(r, nowWall);
              const missing = open && !inWindow;
              const plusDays =
                outMs != null && !Number.isNaN(inMs) && !Number.isNaN(outMs)
                  ? dayDiff(inMs, outMs)
                  : 0;
              const durationMs =
                outMs != null && !Number.isNaN(inMs) && !Number.isNaN(outMs)
                  ? outMs - inMs
                  : null;
              const showEdited = Boolean(r.edited || r.lastEdit);
              const editedLabel =
                r.lastEdit && r.lastEdit.action === "create" && !(r.editCount > 1)
                  ? "Added"
                  : "Edited";

              return (
                <TableRow key={r._id} className={missing ? "aeRow aeRow--missing" : "aeRow"}>
                  <TableCell>
                    <div className="aeEmp">
                      <span className="aeEmpName">{r.name || r.email}</span>
                      <span className="aeEmpEmail">{r.email}</span>
                    </div>
                  </TableCell>
                  <TableCell className="aeNowrap">{fmtDate(r.checkIn)}</TableCell>
                  <TableCell className="aeNowrap">
                    <span className="aeTime">{fmtTime(r.checkIn)}</span>
                  </TableCell>
                  <TableCell className="aeNowrap">
                    {r.checkOut ? (
                      <span className="aeTime">
                        {fmtTime(r.checkOut)}
                        {plusDays > 0 ? (
                          <span
                            className="aeNextDay"
                            title={`Checked out on ${fmtDate(r.checkOut)}`}
                          >
                            +{plusDays} day{plusDays === 1 ? "" : "s"}
                          </span>
                        ) : null}
                      </span>
                    ) : inWindow ? (
                      <Tooltip
                        arrow
                        title={`Still checked in — they can check out themselves until ${fmtTimeMs(
                          inMs + SELF_CHECKOUT_WINDOW_MS
                        )}`}
                      >
                        <span className="aePill aePill--open" tabIndex={0}>
                          Open
                        </span>
                      </Tooltip>
                    ) : (
                      <span className="aePill aePill--missing">
                        <EventBusyOutlinedIcon />
                        Missing
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="aeNowrap">
                    {durationMs != null ? fmtDuration(durationMs) : <span className="aeDash">—</span>}
                  </TableCell>
                  <TableCell className="aeNowrap">
                    <StatusPill status={r.status} />
                  </TableCell>
                  <TableCell align="right" className="aeNowrap">
                    <div className="aeActions">
                      {showEdited ? (
                        <Tooltip arrow title={editSummary(r)}>
                          <span className="aePill aePill--edited" tabIndex={0}>
                            {editedLabel}
                          </span>
                        </Tooltip>
                      ) : null}
                      <Button
                        variant="outlined"
                        size="small"
                        className="aeEditBtn"
                        startIcon={<EditOutlinedIcon />}
                        onClick={() => setDialog({ mode: "edit", record: r })}
                        aria-label={`Edit attendance for ${r.name || r.email} on ${fmtDate(
                          r.checkIn
                        )}`}
                      >
                        Edit
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {dialog ? (
        <EntryDialog
          key={dialog.mode === "edit" ? `edit-${dialog.record._id}` : "create"}
          mode={dialog.mode}
          record={dialog.record}
          employees={employees}
          employeesError={employeesError}
          defaultEmployee={dialog.defaultEmployee}
          onClose={() => setDialog(null)}
          onSaved={handleSaved}
          onStale={handleStale}
        />
      ) : null}

      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toastSeverity} className="aeToast">
          {toastMsg}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AttendanceEditor;
